import React from "react";
import clsx from "clsx";

type ButtonVariant = "primary" | "secondary" | "primaryGlow";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    variant?: ButtonVariant;
    size?: ButtonSize;
    className?: string;
}

/**
 * Reusable Button component (token-based)
 */
export const Button: React.FC<ButtonProps> = ({ children, variant = "primary", size = "md", className, ...props }) => {
    const baseStyles =
        "inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 " +
        "disabled:opacity-60 disabled:cursor-not-allowed";

    const variantStyles: Record<ButtonVariant, string> = {
        primary: "bg-black text-text-inverse hover:bg-black/90",
        secondary: "border border-border-subtle bg-bg-subtle text-text-primary hover:bg-bg-subtle/90",
        primaryGlow: "bg-black text-text-inverse shadow-primary-lg hover:shadow-primary hover:-translate-y-[2px]"
    };

    const sizeStyles: Record<ButtonSize, string> = {
        sm: "px-4 py-2 text-sm",
        md: "px-6 py-3 text-sm",
        lg: "px-10 py-4 text-lg"
    };

    return (
        <button className={clsx(baseStyles, variantStyles[variant], sizeStyles[size], className)} {...props}>
            {children}
        </button>
    );
};
